import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const Section = styled.section`
  width: 100%;
  min-height: 100vh;
  background: #000;
  color: #fff;
  padding: 120px 20px 80px;
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 800px;
  width: 100%;
  font-family: system-ui, -apple-system, sans-serif;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(120deg, #fff, #9ca3af);
  background-size: 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 8s ease-in-out infinite alternate;

  @keyframes gradientMove {
    0% { background-position: 0%; }
    100% { background-position: 100%; }
  }
`;

const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 2.5rem 0 1rem;
  color: #e5e5e5;
`;

const Text = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  opacity: 0.85;
  margin-bottom: 1.2rem;
  color: #ccc;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin: 1rem 0;
  opacity: 0.9;
`;

const ListItem = styled.li`
  margin-bottom: 0.6rem;
  font-size: 1.05rem;
  line-height: 1.6;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.95rem;
  opacity: 0.9;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  vertical-align: top;
`;

const ContactLink = styled.a`
  color: #60a5fa;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const CookiePolicy = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Section>
        <Container>
          <Title>Cookie Policy</Title>
          <Text>Last updated: November 15, 2025</Text>

          <Text>
            This Cookie Policy explains how <strong>Plano AI</strong> ("we", "us", "our") uses cookies and similar technologies 
            when you use our mobile application and website.
          </Text>

          <Subtitle>1. What Are Cookies?</Subtitle>
          <Text>
            Cookies are small text files stored on your device. They help us improve your experience, 
            remember preferences, and analyze how you use Plano AI.
          </Text>

          <Subtitle>2. Types of Cookies We Use</Subtitle>

          <Table>
            <thead>
              <tr>
                <Th>Type</Th>
                <Th>Purpose</Th>
                <Th>Duration</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td><strong>Essential</strong></Td>
                <Td>Enable core functionality (login, navigation)</Td>
                <Td>Session or 1 year</Td>
              </tr>
              <tr>
                <Td><strong>Analytics</strong></Td>
                <Td>Track usage patterns (anonymized)</Td>
                <Td>Up to 2 years</Td>
              </tr>
              <tr>
                <Td><strong>Preferences</strong></Td>
                <Td>Remember theme, language, notifications</Td>
                <Td>1 year</Td>
              </tr>
              <tr>
                <Td><strong>Marketing</strong></Td>
                <Td>Personalized tips & feature highlights</Td>
                <Td>90 days</Td>
              </tr>
            </tbody>
          </Table>

          <Subtitle>3. Third-Party Cookies</Subtitle>
          <Text>We work with trusted partners who may set cookies:</Text>
          <List>
            <ListItem><strong>Firebase Analytics</strong> – app performance and crash reporting</ListItem>
            <ListItem><strong>Mixpanel</strong> – user behavior analysis (anonymized)</ListItem>
            <ListItem><strong>Stripe</strong> – secure payment processing</ListItem>
            <ListItem><strong>Apple / Google</strong> – app store attribution</ListItem>
          </List>

          <Subtitle>4. How to Manage Cookies</Subtitle>
          <Text>You can control cookies through:</Text>
          <List>
            <ListItem><strong>App Settings</strong> → Privacy → disable analytics</ListItem>
            <ListItem><strong>Device Settings</strong> → block third-party cookies</ListItem>
            <ListItem><strong>Browser</strong> → clear cookies in Safari/Chrome</ListItem>
          </List>
          <Text>
            Note: Disabling essential cookies may limit app functionality.
          </Text>

          <Subtitle>5. Your Consent</Subtitle>
          <Text>
            By using Plano AI, you consent to our use of cookies as described. 
            You can withdraw consent at any time via app settings.
          </Text>

          <Subtitle>6. Updates to This Policy</Subtitle>
          <Text>
            We may update this Cookie Policy. Changes will be posted here and notified in-app.
          </Text>

          <Subtitle>7. Contact Us</Subtitle>
          <Text>
            Questions about cookies?<br />
            Email: <ContactLink href="mailto:fluencboy@gmail.com">fluencboy@gmail.com</ContactLink>
          </Text>

          <Text style={{ marginTop: '3rem', fontSize: '0.9rem', opacity: 0.6, textAlign: 'center' }}>
            © 2025 Plano AI. All rights reserved.
          </Text>
        </Container>
      </Section>
    </motion.div>
  );
};

export default CookiePolicy;